import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, BASE_URL, colors } from "../shared/constants";
import { inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";

export function CreateInspectionModal({ siteId, siteArea, pinnedType, onClose, onCreate }) {
  const [inspectionType, setInspectionType] = useState("monthly");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 16));
  const [inspector, setInspector] = useState("");
  const [notes, setNotes] = useState("");
  const [flexible, setFlexible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [technicians, setTechnicians] = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  const [additionalTechs, setAdditionalTechs] = useState([]);

  useEffect(() => {
    axios.get(`${BASE_URL}/auth/technicians/`).then((res) => setTechnicians(res.data)).catch(() => {});
  }, []);

  const toggleTech = (id, name) => {
    const idStr = String(id);
    if (assignedTo === idStr) {
      if (additionalTechs.length > 0) {
        setAssignedTo(additionalTechs[0]);
        setAdditionalTechs(additionalTechs.slice(1));
        const newLead = technicians.find((t) => String(t.id) === additionalTechs[0]);
        if (newLead) setInspector(newLead.first_name && newLead.last_name ? `${newLead.first_name} ${newLead.last_name}` : newLead.username);
      } else {
        setAssignedTo("");
      }
    } else if (additionalTechs.includes(idStr)) {
      setAdditionalTechs(additionalTechs.filter((t) => t !== idStr));
    } else {
      if (!assignedTo) {
        setAssignedTo(idStr);
        if (!inspector.trim()) setInspector(name);
      } else {
        setAdditionalTechs([...additionalTechs, idStr]);
      }
    }
  };

  const handleCreate = async () => {
    if (!inspectionType) {
      setError("Please select an inspection type.");
      return;
    }
    if (!inspector.trim()) {
      setError("Technician name is required.");
      return;
    }
    if (!date) {
      setError("Inspection date is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post(`${API}/inspections/`, {
        site: siteId,
        site_area: siteArea ? siteArea.id : null,
        is_unsectioned_units: !siteArea && pinnedType === "unit_group",
        inspection_type: inspectionType,
        date,
        inspector_name: inspector,
        assigned_to: assignedTo || null,
        additional_techs: additionalTechs.map(Number),
        notes,
        flexible,
        status: "in_progress",
      });
      const inspectionId = res.data.id;

      const efsmsRes = siteArea
        ? await axios.get(`${API}/site-efsms/?area=${siteArea.id}`)
        : await axios.get(`${API}/site-efsms/?site=${siteId}`);
      let scopedEfsms;
      if (siteArea) {
        scopedEfsms = efsmsRes.data;
      } else if (pinnedType === "unit_group") {
        // Unsectioned tenant units: assets live on units with no area of
        // their own, so cross-reference which units qualify.
        const unitsRes = await axios.get(`${API}/site-units/?site=${siteId}`);
        const unsectionedUnitIds = new Set(
          unitsRes.data.filter((u) => !u.area).map((u) => u.id)
        );
        scopedEfsms = efsmsRes.data.filter((e) => e.unit && unsectionedUnitIds.has(e.unit));
      } else {
        scopedEfsms = efsmsRes.data.filter((e) => !e.unit && !e.area);
      }
      const matchingEfsms = scopedEfsms.filter((e) => {
        // An EFSM with no schedule configured at all is always eligible,
        // rather than silently excluded from every inspection type forever.
        const unconfigured = !e.is_monthly && !e.is_bi_annual && !e.is_annual;
        if (inspectionType === "monthly") return e.is_monthly || unconfigured;
        if (inspectionType === "bi_annual") return e.is_bi_annual || unconfigured;
        if (inspectionType === "annual") return e.is_annual || unconfigured;
        return false;
      });
      const assetIds = matchingEfsms.flatMap((e) => (e.assets || []).map((a) => a.id));
      if (assetIds.length > 0) {
        await axios.post(`${API}/inspection-items/bulk_create/`, {
          inspection: inspectionId,
          asset_ids: assetIds,
        });
      }
      onCreate(inspectionId);
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  return (
    <SimpleModal
      title={`Create Inspection${siteArea ? ` — ${siteArea.name}` : ""}`}
      onClose={onClose}
      onSave={handleCreate}
      saving={saving}
      error={error}
      wide
    >
      <div style={fieldStyle}>
        <label style={labelStyle}>Inspection Type *</label>
        <div style={{ display: "flex", gap: "12px" }}>
          {[
            ["monthly", "Monthly"],
            ["bi_annual", "Bi-Annual"],
            ["annual", "Annual"],
          ].map(([val, label]) => (
            <div
              key={val}
              onClick={() => setInspectionType(val)}
              style={{
                flex: 1,
                padding: "12px",
                border: `2px solid ${inspectionType === val ? colors.activeBlue : colors.border}`,
                borderRadius: "8px",
                cursor: "pointer",
                textAlign: "center",
                backgroundColor: inspectionType === val ? "#eff6ff" : "white",
              }}
            >
              <div style={{ fontWeight: "600", color: inspectionType === val ? colors.activeBlue : colors.text }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Inspection Date & Time *</label>
          <input
            style={inputStyle}
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Technician Name (PDF) *</label>
          <input
            style={inputStyle}
            value={inspector}
            onChange={(e) => setInspector(e.target.value)}
            placeholder="e.g. John Smith"
          />
        </div>
      </div>
      {technicians.length > 0 && (
        <div style={fieldStyle}>
          <label style={labelStyle}>Assign Technicians</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {technicians.map((tech) => {
              const idStr = String(tech.id);
              const name = tech.first_name && tech.last_name
                ? `${tech.first_name} ${tech.last_name}`
                : tech.username;
              const isLead = assignedTo === idStr;
              const isAdditional = additionalTechs.includes(idStr);
              const selected = isLead || isAdditional;
              return (
                <button
                  key={tech.id}
                  type="button"
                  onClick={() => toggleTech(tech.id, name)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: "20px",
                    border: `2px solid ${selected ? (isLead ? colors.teal : colors.amber) : colors.border}`,
                    backgroundColor: selected ? (isLead ? "#e0f2fe" : "#fef3c7") : "white",
                    color: selected ? (isLead ? colors.teal : "#92400e") : colors.slate,
                    fontWeight: "600",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  {name}{isLead ? " · Lead" : isAdditional ? " · + Tech" : ""}
                </button>
              );
            })}
          </div>
          {(assignedTo || additionalTechs.length > 0) && (
            <div style={{ fontSize: "11px", color: colors.slate, marginTop: "6px" }}>
              First selected = Lead tech. Tap again to remove.
            </div>
          )}
        </div>
      )}
      <div style={fieldStyle}>
        <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
          <input type="checkbox" checked={flexible} onChange={(e) => setFlexible(e.target.checked)} />
          <span style={{ fontSize: "13px", color: colors.text }}>Flexible — do anytime</span>
        </label>
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>General Notes</label>
        <textarea
          style={{ ...inputStyle, height: "80px", resize: "vertical" }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any general notes about this inspection..."
        />
      </div>
    </SimpleModal>
  );
}
